import { Tooltip } from 'ethos-design-system';
import React, { useState } from 'react';
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector as useReduxSelector,
} from 'react-redux';
import { RootState } from '../../../../app/rootReducer';
import { AppDispatch } from '../../../../app/store';
import {
  useCaseLock,
  useCaseLockActionGuard,
} from '../../../../common/caseLocking/CaseLockContext';
import { NoraDecisionModal } from '../../../../common/components/NoraDecisionModal';
import { TaskIcon } from '../../../../common/components/TaskIcon';
import { useAccess } from '../../../../common/hooks/useAccess';
import { NoraPermissionV2 } from '../../../../common/constants/roles';
import { TaskState } from '../../../../common/constants/states';
import { WaiveMessages } from '../../../../common/constants/waive';
import { ITask } from '../../../tasks/interfaces/ITask';
import { IWaiveReasonResponse } from '../../../tasks/interfaces/IWaiveReasonResponse';
import { fetchWaiveReasons } from '../../../tasks/slice/taskThunks';
import {
  TaskTypes,
  TaskTypeToTaskTitleMapping,
} from '../../../tasks/types/taskTypes';
import styles from '../../styles.module.scss';
export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

export const NoraTaskIcon = ({
  task,
  onTaskStateClick,
  disabled = false,
}: {
  task: ITask;
  onTaskStateClick: (state: TaskState, reason?: string) => void;
  disabled?: boolean;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isLockedForActions, bannerMessage } = useCaseLock();
  const assertCaseActionAllowed = useCaseLockActionGuard();
  const reasons: IWaiveReasonResponse[] = useSelector(
    state => state.taskUi.waiveReasons,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingReasons, setIsLoadingReasons] = useState(false);

  const setWaiveReasonsFetchStatus = (status: string) => {
    setIsLoadingReasons(status === 'loading');
  };
  const canCompleteTask = useAccess({
    permission: NoraPermissionV2.COMPLETE_TASK,
  });

  const canWaive =
    task.state === TaskState.ERROR ||
    task.state === TaskState.ORDERED ||
    (task.state === TaskState.REQUESTED &&
      (task.taskType === TaskTypes.APS || task.taskType === TaskTypes.ETI));
  const canComplete = task.state === TaskState.FOR_REVIEW && canCompleteTask;
  const canOrder =
    task.state === TaskState.REQUESTED &&
    task.taskType !== TaskTypes.APS &&
    task.taskType !== TaskTypes.ETI;
  const taskDisplayString = TaskTypeToTaskTitleMapping[task.taskType];

  const getMessage = () => {
    if (canWaive) {
      return `Waive ${taskDisplayString}`;
    }
    if (canComplete) {
      return `Complete ${taskDisplayString}`;
    }
    if (canOrder) {
      return `Order ${taskDisplayString}`;
    }

    return `No action required`;
  };
  const message = `Waive ${taskDisplayString}`;
  const onStateIconClick = async () => {
    if (!assertCaseActionAllowed()) {
      return;
    }
    if (disabled) {
      return;
    }
    if (canWaive) {
      // Fetch waive reasons with taskType before opening modal
      dispatch(
        fetchWaiveReasons(
          setWaiveReasonsFetchStatus,
          [],
          () => {
            // No-op: errors handled via status callback
          },
          task.taskType,
        ),
      );
      setIsModalOpen(true);
    } else if (canComplete) {
      onTaskStateClick(TaskState.COMPLETED);
    } else if (canOrder) {
      onTaskStateClick(TaskState.ORDERED);
    }
  };

  const onModalCancel = async () => {
    setIsModalOpen(false);
  };

  const onModalDismiss = () => {
    setIsModalOpen(false);
  };

  const onModalSubmit = async (reason: string) => {
    if (reason) {
      onTaskStateClick(TaskState.WAIVED, reason);
    }
    setIsModalOpen(false);
  };

  return (
    <>
      <NoraDecisionModal
        isOpen={isModalOpen}
        onDismiss={onModalDismiss}
        title={message}
        message={WaiveMessages.WAIVE_MESSAGE}
        label={WaiveMessages.WAIVEL_LABEL}
        reasonOptions={reasons}
        onCancel={onModalCancel}
        onSubmit={onModalSubmit}
        isLoading={isLoadingReasons}
      />
      <Tooltip
        popperBoxStyles={styles.NoraTip}
        details={
          isLockedForActions
            ? (bannerMessage ?? 'This case is locked.')
            : getMessage()
        }
        label={`${getMessage()} tooltip`}
      >
        <TaskIcon
          disabled={disabled}
          id={task.id}
          onClick={onStateIconClick}
          state={task.state}
        />
      </Tooltip>
    </>
  );
};
